import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import StudentLayout from "./StudentLayout";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import "./MillionaireGame.css";

// --- LaTeX helpers ---
const hasDelims = (s = "") =>
  /^\$[\s\S]*\$$/.test(s.trim()) ||
  /^\$\$[\s\S]*\$\$/.test(s.trim()) ||
  /^\\\([\s\S]*\\\)$/.test(s.trim()) ||
  /^\\\[[\s\S]*\\\]$/.test(s.trim());

const looksLikeLatex = (s = "") =>
  /\\[a-zA-Z]+|\\frac|\\sqrt|\\binom|\\cdot|\\text|[_^]/.test(s) || hasDelims(s);

// KaTeX <BlockMath math="..."> expects NO delimiters → strip if present
const stripDelims = (s = "") =>
  s.replace(/^\s*(\$\$?|\\\[|\\\()/, "").replace(/(\$\$?|\\\]|\\\))\s*$/, "");


const MillionaireReview = () => {
  const { assignmentId, studentUID } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);

  
  useEffect(() => {
    const fetchData = async () => {
      const submissionId = `${studentUID}_${assignmentId}`;
      const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);
      const submissionSnap = await getDoc(submissionRef);
  
      if (!submissionSnap.exists()) {
        console.warn("No submission found.");
        return;
      }
  
      const submissionData = submissionSnap.data();
      console.log("📄 Submission data:", submissionData);
console.log("🧠 Answers array (from submission):", submissionData.answers);
      const answerData = submissionData.answers || [];
  
      // 🔁 Load only the question IDs from the player's actual journey
      const questionDocs = await Promise.all(
        answerData.map((a) =>
          getDoc(doc(db, "questions", a.questionId))
        )
      );
  
      const loadedQuestions = questionDocs.map((docSnap, index) => {
        const q = docSnap.data();
        console.log(`📦 Question ${index + 1}:`, q);
console.log(`🔍 Matched Answer:`, answerData[index]);
        if (!q) return null;
  
        const correct = q.answers?.[0] || q.answer || "";
        const options = Array.isArray(q.choices) && typeof q.choices[0] === "object"
          ? q.choices
          : (q.choices || []).map((text) => ({
              text,
              isCorrect: text === correct,
            }));
  
        return {
          questionId: answerData[index].questionId,
          text: q.questionText || "",
          latex: q.questionLatex || "",
          optionalQuestionText: q.optionalQuestionText || "",
          paragraphs: q.questionParagraphs || [],
          options,
          correctAnswer: correct,
        };
      }).filter(Boolean);
      console.log("✅ Final ordered questions:", loadedQuestions);

      setQuestions(loadedQuestions);
      setAnswers(answerData);
    };
  
    fetchData();
  }, [assignmentId, studentUID]);
  
  
   
  const currentQuestion = questions[currentQIndex];
  const currentAnswer = answers[currentQIndex];

const renderQuestionContent = (question) => {
  const { paragraphs = [], text = "", latex = "", optionalQuestionText = "" } = question;

  // helper: image url?
  const isImg = (s) => /^https?:\/\/.+\.(png|jpe?g|webp|gif|svg)$/i.test(s || "");

  if (paragraphs.length > 0) {
    return (
      <div style={{ textAlign: "center", lineHeight: 1.6 }}>
        {paragraphs.map((raw, i) => {
          let p = raw;

          // Parse JSON if Firestore stored an object string
          if (typeof p === "string") {
            try {
              const maybe = JSON.parse(p);
              if (maybe && (maybe.text || maybe.image)) p = maybe;
            } catch { /* keep as string */ }
          }

          return (
            <div key={i} style={{ marginBottom: "0.75rem" }}>
              {typeof p === "string" ? (
                looksLikeLatex(p)
                  ? <BlockMath math={stripDelims(p)} />
                  : <p style={{ margin: 0, textAlign: "center" }}>{p}</p>   
              ) : (
                <>
                  {p.text && (
                    looksLikeLatex(p.text)
                      ? <BlockMath math={stripDelims(p.text)} />
                      : <p style={{ margin: 0, textAlign: "center" }}>{p.text}</p>  
                  )}
                  {p.image && (
                    <img
                      src={p.image}
                      alt={`Q${i + 1}`}
                      style={{ maxWidth: "100%", display: "block", margin: "0.5rem auto" }}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // fallback when no paragraphs
  return (
    <div style={{ textAlign: "center", lineHeight: 1.6 }}>
      {text && (
        looksLikeLatex(text)
          ? <BlockMath math={stripDelims(text)} />
          : <p style={{ margin: 0, textAlign: "center" }}>{text}</p>  
      )}
      {latex && <BlockMath math={stripDelims(latex)} />}
      {optionalQuestionText && (
        looksLikeLatex(optionalQuestionText)
          ? <BlockMath math={stripDelims(optionalQuestionText)} />
          : (
            <p style={{ margin: "0.5rem 0 0", textAlign: "center" }}>
              <strong>{optionalQuestionText}</strong>
            </p>
          )
      )}
    </div>
  );
};


  return (
    <StudentLayout>
      <div className="millionaire-wrapper">
        <div className="game-layout">
          <div className="review-header">
            <h2>Review: Question {currentQIndex + 1} of {questions.length}</h2>
          </div>

          <div className="question-block">
            {currentQuestion && renderQuestionContent(currentQuestion)}

            <div className="options-grid">
            {currentQuestion?.options.map((opt, i) => {
  const isCorrect = opt.isCorrect;
  const isSelected = opt.text === currentAnswer?.playerAnswer;

  let className = "option-btn";
  if (isSelected && isCorrect) className += " correct";
  else if (isSelected && !isCorrect) className += " wrong";

  return (
    <button key={i} className={className} disabled>
      <BlockMath math={opt.text} />
    </button>
  );
})}

            </div>

            <div className="working-box" style={{ marginTop: "1rem" }}>
              <h4>📝 Student's Working</h4>
              <textarea
                value={currentAnswer?.working || ""}
                disabled
                style={{ width: "100%", height: "150px", background: "#f5f5f5", color: "white", backgroundColor: "#1c3a6b" }}
              />
            </div>

            <div className="review-nav" style={{ marginTop: "2rem", display: "flex", justifyContent: "space-between" }}>
  {/* Show Previous button ONLY if not on first question */}
  {currentQIndex > 0 && (
    <button onClick={() => {
      setCurrentQIndex(i => i - 1);
      setFirstLoad(false);
    }}>
      ← Previous
    </button>
  )}

  {/* Spacer if needed */}
  <div style={{ flex: 1 }} />

  {/* Show Next button ONLY if not on last question */}
  {currentQIndex < questions.length - 1 && (
    <button onClick={() => {
      setCurrentQIndex(i => i + 1);
      setFirstLoad(false);
    }}>
      Next →
    </button>
  )}
</div>

          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default MillionaireReview;
